/**
 * FeedbackPrompt Component
 * 
 * Prompts users for feedback after 3+ analyses.
 * Simple, non-intrusive modal asking: "Would you trust this tool to help you decide on a deal?"
 */

import React, { useState } from 'react';
import { X, MessageSquare, ThumbsUp, ThumbsDown, HelpCircle, Send } from 'lucide-react';

const FeedbackPrompt = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  lang = 'en' 
}) => {
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [additionalComments, setAdditionalComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  if (!isOpen) return null;
  
  const responses = [
    {
      value: 'yes',
      icon: ThumbsUp,
      label: lang === 'en' ? 'Yes' : 'Sí',
      color: 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
    },
    {
      value: 'not_yet',
      icon: HelpCircle,
      label: lang === 'en' ? 'Not yet' : 'Aún no',
      color: 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100'
    },
    {
      value: 'needs_improvement',
      icon: ThumbsDown,
      label: lang === 'en' ? 'Needs improvement' : 'Necesita mejorar',
      color: 'text-red-600 bg-red-50 border-red-200 hover:bg-red-100'
    }
  ];
  
  const handleSubmit = async () => {
    if (!selectedResponse) return;
    
    setSubmitting(true);
    try {
      await onSubmit({
        trust_response: selectedResponse,
        additional_comments: additionalComments.trim() || null
      });
      setSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDismiss = () => {
    onClose(true); // true = dismissed
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        data-testid="feedback-prompt"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <MessageSquare className="w-6 h-6" />
            <h3 className="text-lg font-semibold">
              {lang === 'en' ? 'Quick Feedback' : 'Comentarios Rápidos'}
            </h3>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
            data-testid="feedback-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {!submitted ? (
            <>
              <p className="text-lg font-medium text-slate-800 mb-6 text-center">
                {lang === 'en' 
                  ? 'Would you trust this tool to help you decide on a deal?' 
                  : '¿Confiarías en esta herramienta para ayudarte a decidir sobre un trato?'}
              </p>
              
              {/* Response Options */}
              <div className="space-y-3 mb-6">
                {responses.map((response) => {
                  const Icon = response.icon;
                  const isSelected = selectedResponse === response.value;
                  
                  return (
                    <button
                      key={response.value}
                      onClick={() => setSelectedResponse(response.value)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? `${response.color} border-current ring-2 ring-current/20`
                          : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                      data-testid={`feedback-option-${response.value}`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? '' : 'text-slate-400'}`} />
                      <span className="font-medium">{response.label}</span>
                    </button>
                  );
                })}
              </div>
              
              {/* Optional Comments */}
              <div className="mb-6">
                <label className="text-sm text-slate-600 mb-2 block">
                  {lang === 'en' 
                    ? 'Any additional thoughts? (optional)' 
                    : '¿Algún comentario adicional? (opcional)'}
                </label>
                <textarea
                  value={additionalComments}
                  onChange={(e) => setAdditionalComments(e.target.value)}
                  placeholder={lang === 'en' ? 'Tell us more...' : 'Cuéntanos más...'}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  data-testid="feedback-comments"
                />
              </div>
              
              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={!selectedResponse || submitting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-blue-600 hover:to-indigo-700 transition-all"
                data-testid="feedback-submit-btn"
              >
                {submitting ? (
                  <span>{lang === 'en' ? 'Submitting...' : 'Enviando...'}</span>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>{lang === 'en' ? 'Submit Feedback' : 'Enviar Comentarios'}</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ThumbsUp className="w-8 h-8 text-emerald-600" />
              </div>
              <h4 className="text-xl font-semibold text-slate-800 mb-2">
                {lang === 'en' ? 'Thank you!' : '¡Gracias!'}
              </h4>
              <p className="text-slate-600">
                {lang === 'en' 
                  ? 'Your feedback helps us improve.' 
                  : 'Tu feedback nos ayuda a mejorar.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackPrompt;
